// Таблицата с редовете на A4 хартията на фактурата (преглед + браузърен печат).
// Изнесена от `InvoicePreviewModal` при добавянето на бакшиша (Tailwind).
//
// Хартията е ВИНАГИ светла (печатен документ) — цветовете са литерали, не
// theme токени (виж скила cloudts-theming §Нов печатен документ).
//
// Нетото на реда идва от споделената ДДС разбивка (lib/vat.ts) — сумира
// ТОЧНО на тоталите отдолу. Редът за бакшиш без ДДС (`line_kind: "tip"` със
// ставка 0 — група А): в колоната „ДДС %" стои „—", а под името — „ДДС не се
// начислява — доброволен бакшиш.". Облагаемият бакшиш (група Б/В) е със
// ставката си, като продуктов ред. Фактура без бакшиш изглежда точно както досега.

import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import type { InvoiceLine } from "../../../../../api/invoices";
import { netUnitPrice, type VatBreakdown } from "../../../../../lib/vat";

const formatMoney = (value: number | string) => {
  const parsed = typeof value === "number" ? value : parseFloat(String(value));
  if (!Number.isFinite(parsed)) return "0.00";
  return parsed.toFixed(2);
};

const formatQty = (value: number | string) => {
  const parsed = typeof value === "number" ? value : parseFloat(String(value));
  if (!Number.isFinite(parsed)) return "0";
  return Number.isInteger(parsed) ? String(parsed) : parsed.toFixed(3);
};

type Align = "left" | "right";

const HeaderCell = ({
  children,
  widthClass = "",
  align,
}: {
  children: ReactNode;
  widthClass?: string;
  align: Align;
}) => (
  <th
    className={`${widthClass} ${
      align === "left" ? "text-left" : "text-right"
    } border-b border-[#C9C7BF] px-[10px] py-[8px] text-[11px] font-bold uppercase tracking-[0.4px] text-[#5C5A52]`}
  >
    {children}
  </th>
);

const BodyCell = ({
  children,
  align,
  mono = false,
  bold = false,
}: {
  children: ReactNode;
  align: Align;
  mono?: boolean;
  bold?: boolean;
}) => (
  <td
    className={`${align === "left" ? "text-left" : "text-right"} px-[10px] py-[7px] ${
      mono ? "font-pos-mono tabular-nums" : ""
    } ${bold ? "font-bold" : "font-normal"}`}
  >
    {children}
  </td>
);

type Props = {
  lines: InvoiceLine[];
  breakdown: VatBreakdown;
};

export const InvoicePaperLines = ({ lines, breakdown }: Props) => {
  const { t } = useTranslation();

  return (
    <table className="mb-3 w-full border-collapse text-[12.5px]">
      <thead>
        <tr className="bg-[#EDECE8]">
          <HeaderCell widthClass="w-[40px]" align="left">
            №
          </HeaderCell>
          <HeaderCell align="left">{t("invoicePreview.colDescription")}</HeaderCell>
          <HeaderCell widthClass="w-[70px]" align="right">
            {t("invoicePreview.colVatRate")}
          </HeaderCell>
          <HeaderCell widthClass="w-[90px]" align="right">
            {t("invoicePreview.colQty")}
          </HeaderCell>
          <HeaderCell widthClass="w-[84px]" align="right">
            {t("invoicePreview.colUnitPrice")}
          </HeaderCell>
          <HeaderCell widthClass="w-[84px]" align="right">
            {t("invoicePreview.colTotal")}
          </HeaderCell>
        </tr>
      </thead>
      <tbody>
        {lines.map((line, index) => {
          const isTip = line.line_kind === "tip" && !(Number(line.vat_rate) > 0);
          return (
            <tr key={line.id} className="border-b border-[#E5E3DC]">
              <BodyCell align="left">{index + 1}</BodyCell>
              <BodyCell align="left">
                {line.name}
                {isTip ? (
                  <span className="mt-0.5 block text-[11px] italic text-[#5C5A52]">
                    {t("invoiceTips.vatNote")}
                  </span>
                ) : null}
              </BodyCell>
              <BodyCell align="right" mono>
                {isTip ? "—" : `${Number(line.vat_rate)}%`}
              </BodyCell>
              <BodyCell align="right" mono>
                {formatQty(line.quantity)}
              </BodyCell>
              <BodyCell align="right" mono>
                {formatMoney(
                  isTip
                    ? Number(line.unit_price)
                    : netUnitPrice(Number(line.unit_price), Number(line.vat_rate)),
                )}
              </BodyCell>
              <BodyCell align="right" mono bold>
                {formatMoney(breakdown.lines[index].net)}
              </BodyCell>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};
